import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, Search, UserPlus, Edit2, Trash2, Key, 
  CheckCircle, XCircle 
} from 'lucide-react';
import DualScrollTable from '../../components/common/DualScrollTable';
import { userService } from '../../services/userService';
import EditUserModal from './modals/EditUserModal';
import DeleteUserModal from './modals/DeleteUserModal';
import ResetPasswordModal from './modals/ResetPasswordModal';
import type { User } from '../../types/auth';

export default function UserManagement() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalState, setModalState] = useState<'edit' | 'delete' | 'reset' | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: userService.getAllUsers
  });

  const createUserMutation = useMutation({
    mutationFn: userService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModalState(null);
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: userService.updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModalState(null);
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setModalState(null);
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) => 
      userService.resetPassword({ userId, newPassword }),
    onSuccess: () => {
      setModalState(null);
    }
  });

  const filteredUsers = users.filter(user => 
    user.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Users className="h-6 w-6 text-gray-400 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">User Management</h2>
        </div>
        <button
          onClick={() => {
            setSelectedUser(null);
            setModalState('edit');
          }}
          className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
        >
          <UserPlus className="h-5 w-5 mr-2" />
          Add User
        </button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <DualScrollTable>
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {user.role.replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {user.isActive ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                          <span className="text-green-800">Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-5 w-5 text-red-500 mr-2" />
                          <span className="text-red-800">Inactive</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setModalState('edit');
                      }}
                      className="text-primary hover:text-primary-dark mr-4"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setModalState('reset');
                      }}
                      className="text-yellow-600 hover:text-yellow-700 mr-4"
                    >
                      <Key className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setModalState('delete');
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </DualScrollTable>
        </div>
      )}

      {modalState === 'edit' && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setSelectedUser(null);
            setModalState(null);
          }}
          onSave={async (userData: User & { password?: string }) => {
            if (selectedUser) {
              await updateUserMutation.mutateAsync(userData);
            } else {
              await createUserMutation.mutateAsync(userData as User & { password: string });
            }
          }}
        />
      )}

      {modalState === 'delete' && selectedUser && (
        <DeleteUserModal
          user={selectedUser}
          onClose={() => {
            setSelectedUser(null);
            setModalState(null);
          }}
          onConfirm={async () => {
            await deleteUserMutation.mutateAsync(selectedUser.id);
          }}
        />
      )}

      {modalState === 'reset' && selectedUser && (
        <ResetPasswordModal
          user={selectedUser}
          onClose={() => {
            setSelectedUser(null);
            setModalState(null);
          }}
          onConfirm={async (newPassword) => {
            await resetPasswordMutation.mutateAsync({
              userId: selectedUser.id,
              newPassword
            });
          }}
        />
      )}
    </div>
  );
}
